import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useScrollBlock } from 'hooks';
import { useGetSharesWalletsQuery, useInvestorInvestingEntitiesQuery } from 'services/apollo';
import { InvestorBuyPropertyShareType } from 'services/apollo/multisto';
import { FetchFees } from 'services/apollo/fee';

import { Col, Input, Loading, Row } from 'atoms';
import { AppData } from 'services/apollo/core';
import { CalculateShareFeesAndTotal, stripToNumber } from 'lib/utils';
import { DiscountQueryResult } from 'services/apollo/discount';
import * as math from 'mathjs';
import BuyButton from './components/BuyButton';
import PriceTransformerDetails, { TransformerType } from './components/priceTransformer/PriceTransformerDetails';

interface PropertyShareProps {
  sto: number;
  share: InvestorBuyPropertyShareType;
  hasFunds: boolean;
  fee: FetchFees[];
  discounts: DiscountQueryResult[];
  setLoadingRequest: (isActive: boolean) => void;
  appData: AppData;
}

const BuyPropertyShare: React.FC<PropertyShareProps> = (props) => {
  const { sto, share, hasFunds, fee, discounts, setLoadingRequest, appData } = props;
  const [value, setValue] = useState<number>(share.minimumSharesToBuyByInvestor);
  const [totalPrice, setTotalPrice] = useState<number>(share.totalPrice * share.minimumSharesToBuyByInvestor);
  const [feePrice, setFeePrice] = useState<number>(0);
  const [discountPrice, setDiscountPrice] = useState<number>(0);
  const { i18n, t } = useTranslation();
  const location = useLocation<{ shareId: number; value: number; text: string }>();

  const history = useHistory();
  const { data, loading } = useInvestorInvestingEntitiesQuery();

  const { data: whiteListedWallets } = useGetSharesWalletsQuery({
    variables: { shareTypeID: share.ID },
    fetchPolicy: 'no-cache',
  });
  const changeValueFromInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (share.maximumSharesToBuyByInvestor && +e.currentTarget.value > share.maximumSharesToBuyByInvestor) {
      e.currentTarget.value = String(share.maximumSharesToBuyByInvestor);
    }
    const amount = stripToNumber(share.blockchaindecimals, e);
    changeValue(amount);
  };
  const changeValue = (amount: number) => {
    setValue(amount);
    if (((fee.length && isFeesEnabled) || (discounts.length && isDiscountsEnabled)) && amount) {
      const calculateFeesAndTotal = CalculateShareFeesAndTotal({
        fee,
        discounts,
        shareTotalPrice: share.totalPrice,
        amount,
        isSellBack: false,
        isFeesEnabled,
        isDiscountsEnabled,
      });
      setFeePrice(calculateFeesAndTotal.fees);
      setDiscountPrice(calculateFeesAndTotal.discounts);
      setTotalPrice(calculateFeesAndTotal.total);
    } else {
      setTotalPrice(share.totalPrice * amount);
    }
  };

  const { investorInvestingEntities: entities } = data || {};
  const [blockScroll, allowScroll] = useScrollBlock();

  const isWalletDropDown = share.isBlockchain && share.channelIDForAutoPayments !== -1;

  if (history.location.state !== undefined) {
    if (location?.state?.shareId === share.ID) {
      changeValue(location.state.value);
      window.history.replaceState(undefined, '');
      history.location.state = null;
    }
  }

  if (loading || !entities || !whiteListedWallets) {
    return <Loading />;
  }

  const investorWallet = whiteListedWallets.getSharesWallets.find((e) => !e.isBlocked);
  const { isDiscountsEnabled, isFeesEnabled } = appData;
  return (
    <>
      <Row>
        <Col md={4}>
          <p>{t('BuyPropertyShare-SharesAvailable')}</p>
        </Col>
        <Col md={8}>
          <p>{(share.availableShare ?? 0).toLocaleString(i18n.language)}</p>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <p>{t('BuyPropertyShare-price/share')}</p>
        </Col>
        <Col md={8}>
          <p>
            {share.currency.symbol} {(share.totalPrice ?? 0).toLocaleString(i18n.language)}
          </p>
        </Col>
      </Row>
      {share.maximumSharesToBuyByInvestor > 0 ? (
        <Row>
          <Col md={4}>
            <p>{t('BuyPropertyShare-maximumSharesToBuyByInvestor')}</p>
          </Col>
          <Col md={8}>
            <p>{share.maximumSharesToBuyByInvestor}</p>
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col md={4}>
          <p>{t('BuyPropertyShare-minimumShareQuantityLabel')}</p>
        </Col>
        <Col md={8}>
          <p>{(share.minimumSharesToBuyByInvestor ?? 0).toLocaleString(i18n.language)}</p>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md={4}>
          <p>{t('BuyPropertyShare-numberOfSharesToBuy')}</p>
        </Col>
        <Col md={4}>
          <Input
            type="number"
            min={0}
            max={share.maximumSharesToBuyByInvestor}
            maxlength
            step={1}
            onChange={changeValueFromInput}
            onFocus={blockScroll}
            onBlur={allowScroll}
            value={value || ''}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <p>{t('BuyPropertyShare-subTotalPrice')}</p>
        </Col>
        <Col md={4}>
          <p>
            {share.currency.symbol}{' '}
            {(math.multiply(share.totalPrice, value) ?? 0).toLocaleString(i18n.language, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}
          </p>
        </Col>
      </Row>
      {isFeesEnabled ? (
        <PriceTransformerDetails
          currencySymbol={share.currency.symbol}
          transformerPrice={feePrice}
          transformer={{ type: TransformerType.FEE, fees: fee }}
          subtotal={math.multiply(value, share.totalPrice)}
        />
      ) : null}
      {isDiscountsEnabled ? (
        <PriceTransformerDetails
          currencySymbol={share.currency.symbol}
          transformerPrice={discountPrice}
          transformer={{ type: TransformerType.DISCOUNT, discounts }}
          subtotal={math.multiply(value, share.totalPrice)}
        />
      ) : null}
      <Row>
        <Col md={4}>
          <p>{t('BuyPropertyShare-totalPrice')}</p>
        </Col>
        <Col md={4}>
          <p>
            {share.currency.symbol}{' '}
            {(totalPrice ?? 0).toLocaleString(i18n.language, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}
          </p>
        </Col>
      </Row>
      {isWalletDropDown && investorWallet ? (
        <>
          <Row>
            <Col md={4}>
              <div className="mt-3">{t('BuyPropertyShare-receiver-wallet')}</div>
            </Col>
            <Col>
              <div className="mt-3">{investorWallet.publicKey}</div>
            </Col>
          </Row>
        </>
      ) : null}
      <BuyButton
        sto={sto}
        hasFunds={hasFunds}
        setLoadingRequest={setLoadingRequest}
        appData={appData}
        isWalletDropDown={isWalletDropDown}
        shareCount={value}
        shareType={share}
        publicKey={investorWallet?.publicKey?.toString()}
      />
    </>
  );
};

export default BuyPropertyShare;
